import React, { useEffect, useRef, useState } from 'react';
import { PlaybackProvider } from '../../hooks/usePlayback';
import { AutoAdvance } from './AutoAdvance';
import { PlayerTimer } from './PlayerTimer';
import { PlayerControls } from './PlayerControls';
import { Page1Contract } from './screens/Page1Contract';
import { Page2View } from './screens/Page2View';
import { Page3Reveal } from './screens/Page3Reveal';
import { Page4Insight } from './screens/Page4Insight';
import { Page5Product } from './screens/Page5Product';
import { Page6Control } from './screens/Page6Control';
import { Page7Bonus } from './screens/Page7Bonus';
import { BRAND, BRAND_LOGO } from '../../data/teaser';
import { TRACKING_SECONDS } from '../../data/tracking';
type Page = 'contract' | 'view' | 'reveal' | 'insight' | 'product' | 'control' | 'bonus';
const ORDER: Page[] = ['contract', 'view', 'reveal', 'insight', 'product', 'control', 'bonus'];

/** Un-paused milliseconds spent on each page before the story moves itself on. */
const DWELL: Partial<Record<Page, number>> = {
  view: 7000,
  reveal: 5000,
  insight: 5500,
  product: 5500,
  control: 4000
};
const TICK_MS = 100;

/**
 * The tracking-led edition, presented as an interactive video: the clinical
 * problem first, the attachment as one variable inside it, Precision Grip as
 * the practical answer, time saved last.
 */
export function TrackingTeaser() {
  const [page, setPage] = useState<Page>('contract');
  const [remaining, setRemaining] = useState(TRACKING_SECONDS);
  const [running, setRunning] = useState(false);
  const [paused, setPaused] = useState(false);
  /** True while the viewer is answering the on-page question. */
  const [held, setHeld] = useState(false);
  /** Once the viewer scrubs the story themselves, it stops moving on its own. */
  const [manual, setManual] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const frozen = paused || held;
  useEffect(() => {
    if (!running || frozen) return;
    intervalRef.current = setInterval(() => {
      setRemaining(prev => Math.max(0, prev - TICK_MS / 1000));
    }, TICK_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [running, frozen]);
  useEffect(() => {
    if (running && remaining <= 0) setRunning(false);
  }, [running, remaining]);
  const begin = () => {
    setPaused(false);
    setManual(false);
    setRunning(true);
    setPage('view');
  };
  const advance = () => {
    const next = ORDER[ORDER.indexOf(page) + 1];
    if (!next) return;
    if (next === 'bonus') {
      setRunning(false);
      setRemaining(0);
    }
    setHeld(false);
    setPage(next);
  };
  const goBack = () => {
    const previous = ORDER[ORDER.indexOf(page) - 1];
    if (!previous || previous === 'contract') return;
    setManual(true);
    setRunning(false);
    setHeld(false);
    setPage(previous);
  };
  const goNext = () => {
    if (page === 'bonus') return;
    if (page === 'contract') {
      begin();
      setManual(true);
      return;
    }
    setManual(true);
    advance();
  };
  const restart = () => {
    setRunning(false);
    setPaused(false);
    setHeld(false);
    setManual(false);
    setRemaining(TRACKING_SECONDS);
    setPage('contract');
  };
  const pageIndex = ORDER.indexOf(page);
  const dwell = manual ? undefined : DWELL[page];
  const playing = !paused && !held && !manual && page !== 'contract' && page !== 'bonus';
  const togglePlay = () => {
    if (page === 'contract') {
      begin();
      return;
    }
    if (held) {
      setHeld(false);
      return;
    }
    if (manual && page !== 'bonus') {
      setManual(false);
      setPaused(false);
      if (remaining > 0) setRunning(true);
      return;
    }
    setPaused(prev => !prev);
  };
  return <div className="relative h-full w-full overflow-hidden bg-solventum-bg selection:bg-solventum-primary selection:text-solventum-bg" data-id="element-250" data-mp="3i5vi10a2olih">
      <PlayerTimer remaining={remaining} paused={frozen} data-id="element-251" data-mp="3delss0sfrk74" />

      <PlaybackProvider paused={frozen} data-id="element-252" data-mp="2xxnjz9p59b5x">
        <div key={page} className="h-full w-full animate-push-in" data-id="element-253" data-mp="2xwm8j447umno">
          {dwell !== undefined && <AutoAdvance after={dwell} onDone={advance} data-id="element-254" data-mp="32xid7jwe6cu5" />}
          {page === 'contract' && <Page1Contract onBegin={begin} data-id="element-255" data-mp="2oxh9lwoqsraz" />}
          {page === 'view' && <Page2View onHold={setHeld} onContinue={advance} data-id="element-256" data-mp="2syau0g2z7nz4" />}
          {page === 'reveal' && <Page3Reveal data-id="element-257" data-mp="3gnvfb5mlqxbk" />}
          {page === 'insight' && <Page4Insight data-id="element-258" data-mp="3gp6k98rsab49" />}
          {page === 'product' && <Page5Product data-id="element-259" data-mp="3lmylitebj9l4" />}
          {page === 'control' && <Page6Control data-id="element-260" data-mp="3llnbibua55lk" />}
          {page === 'bonus' && <Page7Bonus data-id="element-261" data-mp="37vjrzsmsbpzt" />}
        </div>
      </PlaybackProvider>

      <img src={BRAND_LOGO} alt={BRAND} className="pointer-events-none absolute bottom-12 left-6 z-10 h-4 w-auto object-contain opacity-40 md:bottom-14 md:left-14 md:h-5" data-id="element-262" data-mp="37vbebyiu8gnk" />

      <PlayerControls playing={playing} onTogglePlay={togglePlay} onBack={goBack} onNext={goNext} onRestart={restart} canGoBack={pageIndex > 1} canGoNext={page !== 'bonus'} progress={pageIndex / (ORDER.length - 1)} markers={ORDER.slice(1, -1).map((_, index) => (index + 1) / (ORDER.length - 1))} data-id="element-263" data-mp="3g27fmq9o19j" />
    </div>;
}